<template lang="pug">
#standard-account-list(v-cloak)
  .box
    .box-header.with-border.register-header
      .title
        span 一般ユーザー管理
      .buttons
        button.btn.add-btn(@click="gotoRegister" type="button") 一般ユーザー作成
    .box-body
      .note.mb-2
        span 一般ユーザーを作成すると、HitomeQコネクト管理システムを使用するユーザーを増やすことができます。
        br
        span 作成した一般ユーザーごとに、管理者ユーザーが権限（使用機能の使用制限など）を設定することができます。
      AccountTable.account-table(
        v-model="accountItems"
        :fields="accountFields"
        :totalRows.sync="rows"
        :currentPage="currentPage"
        :perPage="perPage"
        :isLoading="isLoading"
        :filter="filter"
        @onEdit="gotoStandardAccountEdit"
        @onDelete="showConfirmDeleteModal"
      )
      .table-options.d-flex.flex-row.justify-content-end.align-items-center
        // 検索
        .search-filter.d-flex.mr-1.align-items-center.mr-2
          b-input-group.search-filter-input-group.mr-1(size="md")
            b-input-group-prepend
              b-input-group-text.search-filter-icon
                .search-icon
            b-form-input.search-filter-input(v-model="filter.searchWord" type="search" placeholder="検索ワードを入力してください")
          HelpIcon.d-flex.justify-content-end.align-items-center(popoverId="search-filter-help" title="検索項目について")
            template(v-slot:text)
              div 検索項目は以下になります。
              div ・ユーザーID
              div ・氏名
        // ページあたりの行数
        span.table-option-text 1ページの行数:
        .per-page
          b-form-select(
            id="per-page-select"
            v-model="perPage"
            :options="pageOptions"
            size="md"
            @change="selectPerPage"
          )
        // ページネーション
        b-pagination.m-0(
          v-model="currentPage"
          :total-rows="rows"
          :per-page="perPage"
          :disabled="isLoading"
          @change="selectPage"
          hide-goto-end-buttons
        )

  OkCancelModal(v-model="showDeleteModal" :title="deleteMessage" @ok="deleteUser")
  OkModal(v-model="showDeleteSuccessModal" title="削除に成功しました。" @ok="createView")
  PageLoading(:isBusy="isBusy" :message="loadingMessage")
</template>

<script lang="ts">
import { Component, Inject, Watch, Vue } from "vue-property-decorator";
import { userStore, editAccountStore } from "@/store/store";
import { errorHandle } from "@/errorHandler";

import Setting from "../../../../public/setting.json";
import { PcUrl } from "@/commons/constants/PcUrl";
import StandardAccountsService, { StandardAccountsGetModel } from "@/commons/services/StandardAccounts";

import OkCancelModal from "@/pc/basics/OkCancelModal.vue";
import OkModal from "@/pc/basics/OkModal.vue";
import AccountTable, { AccountTableField, AccountTableItem, FilterSetting } from "@/pc/containers/settings/AccountTable.vue";
import PageLoading from "@/pc/basics/PageLoading.vue";
import HelpIcon from "@/pc/basics/HelpIcon.vue";

@Component({
  components: {
    AccountTable,
    OkCancelModal,
    OkModal,
    HelpIcon,
    PageLoading
  }
})
export default class StandardAccountList extends Vue {
  /** 一般アカウントサービス */
  @Inject("StandardAccountsService") standardAccountsService!: StandardAccountsService;

  /** ページ全体ローディング表示可否 */
  isBusy: boolean = false;
  /** ローディングメッセージ */
  loadingMessage: string = "";
  /** 削除確認モーダル表示可否 */
  showDeleteModal: boolean = false;
  /** 削除完了モーダル表示可否 */
  showDeleteSuccessModal: boolean = false;

  /** 一般ユーザー列名 */
  accountFields: Array<AccountTableField> = [];
  /** 一般ユーザー各行の情報 */
  accountItems: Array<AccountTableItem> = [];
  /** テーブルのローディング表示可否 */
  isLoading: boolean = true;
  /** 削除対象一般ユーザーのユーザーID */
  deleteTargetUserId: string = "";
  /** 削除確認メッセージ */
  deleteMessage: string = "";

  /** フィルタリング条件 */
  filter: FilterSetting = { searchWord: "" };
  /** ページングの現在ページ番号 */
  currentPage: number = 1;
  /** 現在取得している件数 */
  currentRows: number = 0;
  /** ページングの全体行数 */
  rows: number = 0;
  /** 1ページに表示する行数 */
  perPage: number = Number(Setting.standardAccount.perPage);
  /** 1ページに表示する行数の選択項目 */
  pageOptions: number[] = Setting.standardAccount.pageOptions;
  /** 先行して取得するページ範囲 */
  pageRange: number = Setting.standardAccount.pageRange;

  /**
   * ### 1ページあたりの表示行数の変更イベント
   *
   * - 変更をクッキーに持たせる
   */
  @Watch("perPage")
  public changePerPage(perPage: number): void {
    this.setCookie(perPage);

    return;
  }

  /**
   * ### 1ページあたりの表示行数をCookieに設定する
   */
  public setCookie(perPage: number): void {
    // Cookie有効秒数。有効期限は必ず設定しないといけないので、10年分にしておく
    const saveSecond = 10 * 365 * 24 * 60 * 60;
    document.cookie = `standard-account-list-per-page=${perPage}; max-age=${saveSecond};`;

    return;
  }

  /**
   * ### vue lifecycle mounted
   */
  public async mounted(): Promise<void> {
    // 画面初期表示時に作成して変更しないものを初期化する
    const accountFields: Array<AccountTableField> = this.createAccountFields();
    this.accountFields = accountFields;
    // 1ページあたりの表示行数はCookieから取得する。取得できない場合はデフォルト値を使用する
    const perPage: number | null = this.loadCookie();
    this.perPage = perPage ? perPage : Number(Setting.standardAccount.perPage);

    // 動的に変更する画面情報を作成する
    this.accountItems = [];
    this.currentRows = this.accountItems.length;
    this.currentPage = 1;

    await this.createView();

    return;
  }

  /**
   * ### 一般ユーザーの列名を作成する
   */
  public createAccountFields(): Array<AccountTableField> {
    let fields: Array<AccountTableField> = [];

    fields.push({ key: "userId", label: "ユーザーID", sortable: false, sortDirection: "desc", class: "user-id" });
    fields.push({ key: "name", label: "ユーザー名", sortable: false, sortDirection: "desc", class: "name" });
    fields.push({ key: "operation", label: "登録情報", sortable: false, sortDirection: "desc", class: "text-center operation" });

    return fields;
  }

  /**
   * ### Cookieを見て、値が保持されている場合はCookieの値を返す
   */
  public loadCookie(): number | null {
    if (document.cookie) {
      let cookieMap: any = {};
      let cookies: Array<string> = document.cookie.split("; ");
      cookies.forEach(function (value) {
        //cookie名と値に分ける
        let content = value.split("=");
        cookieMap[content[0]] = content[1];
      });

      // cookieに値が格納されていないとundefinedが格納されているが、実際にはゴミデータが格納されているのでisNaN()でチェックが必要
      const perPage: number = cookieMap["standard-account-list-per-page"];

      return isNaN(perPage) ? null : perPage;
    }

    return null;
  }

  /**
   * ### 画面表示情報を作成する
   */
  public async createView(): Promise<void> {
    this.isLoading = true;

    // 全体の行数を非同期で取得する
    this.setRows();

    // 一般ユーザーテーブルに渡す各プロパティを作成する
    const accounts: Array<StandardAccountsGetModel> = await this.getAccounts();
    const accountItems: Array<AccountTableItem> = this.createAccountItems(accounts);
    this.accountItems = this.accountItems.concat(accountItems);
    this.currentRows = this.accountItems.length;

    this.isLoading = false;

    return;
  }

  /**
   * ### ページング用の全件数を設定する
   */
  public async setRows() {
    try {
      const count: number = await this.standardAccountsService.getCount(userStore);
      this.rows = count;
    } catch (error) {
      // 本処理はawaitされない非同期処理なので、共通エラーハンドラーが例外をキャッチしない
      // 例外発生時は自分でエラーハンドラーを呼ぶ
      errorHandle(error, this);
    }

    return;
  }

  /**
   * ### アカウントを取得する
   */
  public async getAccounts(): Promise<Array<StandardAccountsGetModel>> {
    const startIndex: number = this.currentRows;
    // tableのライブラリでソートやフィルタリング、ページングを管理するので表示対象のよりも多めに取得しておく
    const endIndex: number = this.perPage * (this.currentPage * this.pageRange);
    const params: any = {
      startIndex: startIndex,
      endIndex: endIndex
    };
    const response: Array<StandardAccountsGetModel> = await this.standardAccountsService.get(userStore, params);

    return response;
  }

  /**
   * ### 一般ユーザーの行を作成する
   */
  public createAccountItems(accounts: Array<StandardAccountsGetModel>): Array<AccountTableItem> {
    let items: Array<AccountTableItem> = [];

    for (const account of accounts) {
      items.push({
        userId: account.userId,
        name: account.name,
        kana: account.kana
      });
    }

    return items;
  }

  /**
   * ### 1ページあたりの表示行数変更イベント
   *
   * - 1ページあたりの表示行数を更新
   * - ページあたりの表示行数が変わるので表の情報を作り直す
   */
  public async selectPerPage(perPage: number): Promise<void> {
    this.perPage = perPage;
    this.currentPage = 1;
    this.accountItems = [];
    this.currentRows = this.accountItems.length;
    await this.createView();

    return;
  }

  /**
   * ### ページネーションのページ番号押下
   */
  public async selectPage(page: number): Promise<void> {
    this.currentPage = page;

    // 初期表示時点では一定数しか情報を取得していないので、既に情報が取得されていなければ取得する
    const newPageRows = this.perPage * (this.currentPage - 1);
    if (this.currentRows <= newPageRows) {
      await this.createView();
    }

    return;
  }

  /**
   * ### 一般ユーザー編集画面へ遷移
   */
  public gotoStandardAccountEdit(account: AccountTableItem): void {
    // URLにuserIdを含めないために、storeに編集対象のuserIdを保持する
    editAccountStore.setUserId({ userId: account.userId });
    this.$router.push(PcUrl.StandardAccountEdit);

    return;
  }

  /**
   * ### 削除確認モーダルを表示する
   *
   * - 削除メッセージは動的に作成する
   */
  public showConfirmDeleteModal(account: AccountTableItem): void {
    this.deleteMessage = `${account.userId}のユーザー情報を削除しますか？`;
    this.showDeleteModal = true;

    this.deleteTargetUserId = account.userId;

    return;
  }

  /**
   * ### 一般ユーザーを削除する
   */
  public async deleteUser(): Promise<void> {
    this.isBusy = true;
    this.loadingMessage = "削除中...";

    await this.standardAccountsService.delete(userStore, this.deleteTargetUserId);

    // 削除成功ダイアログを表示する
    this.showDeleteSuccessModal = true;
    this.isBusy = false;

    // ページに表示する情報を取得しなおす
    this.accountItems = [];
    this.currentRows = this.accountItems.length;
    // 削除による画面の再描画もあるので、現在ページ番号はここで初期化する
    this.currentPage = 1;
    await this.createView();

    return;
  }

  /**
   * ### 一般ユーザー新規作成画面へ遷移する
   */
  public gotoRegister(): void {
    this.$router.push({ name: PcUrl.StandardAccountRegister });
  }
}
</script>

<style lang="scss" scoped>
// 親要素からネストしてスタイルを指定する
#standard-account-list {
  max-width: 1280px;
  margin-left: auto;
  margin-right: auto;
}
</style>
